import "@/App.css";
import { Phone, MapPin, Clock, Star, Sparkles, Heart, Check, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";

// Business Constants
const BUSINESS_INFO = {
  name: "Hertime Eyelash Extensions",
  tagline: "Maylands",
  phone: "0402 337 026",
  phoneLink: "tel:0402337026",
  address: "Unit 6/7 Kelvin St, Maylands WA 6051",
  rating: 5.0,
  reviewCount: 40,
  hours: [
    { day: "Monday", time: "8am–8pm" },
    { day: "Tuesday", time: "8am–8pm" },
    { day: "Wednesday", time: "8am–8pm" },
    { day: "Thursday", time: "8am–8pm" },
    { day: "Friday", time: "8am–8pm" },
    { day: "Saturday", time: "Closed" },
    { day: "Sunday", time: "8am–8pm" },
  ],
  googleMapsUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3385.0!2d115.898!3d-31.940!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2a32bae5b7ca61f1%3A0x4c5b7bb87a40b0c4!2s6%2F7%20Kelvin%20St%2C%20Maylands%20WA%206051!5e0!3m2!1sen!2sau!4v1704425600000",
  heroImage: "https://lh3.googleusercontent.com/p/AF1QipNVcKIQGNkwVb239537tJo7k4VTjmiAxQdzLJw3=s680-k-no",
};

// Reviews data
const REVIEWS = [
  {
    name: "Miku",
    date: "2 weeks ago",
    text: "Her lash work is absolutely amazing — I truly love her hybrid lashes and the results always look perfect. She gives professional and honest advice based on what genuinely suits your eyes, which makes me trust her completely. It&apos;s clear that she really knows what she&apos;s doing, and that level of expertise makes such a big difference.",
  },
  {
    name: "N J",
    date: "2 weeks ago",
    text: "Her skills are extremely reliable. Even after three weeks, my eyelash extensions are still in place and don&apos;t come off easily. Despite the high quality, the price is very reasonable. The room is cute, clean, and beautifully styled, and the lovely background music helps me relax so much that I often fall asleep.",
  },
  {
    name: "Jay",
    date: "a month ago",
    text: "Incredible work! I&apos;ve been getting lashed for years, and after taking a break during pregnancy I was finally ready for a new set. Millie was able to create exactly what I wanted. They are incredibly well done and very comfortable. If you want beautiful lashes at a great price, Hertime Eyelash Extensions is for you!",
  },
];

// Services data
const SERVICES = [
  {
    title: "Classic Lashes",
    description: "Natural, elegant enhancement that adds subtle length and definition to your lashes.",
    icon: Sparkles,
  },
  {
    title: "Hybrid Lashes",
    description: "The perfect blend of classic and volume techniques for a textured, wispy look.",
    icon: Heart,
  },
  {
    title: "Volume Lashes",
    description: "Dramatic, full lashes that create stunning depth and glamorous impact.",
    icon: Star,
  },
];

// Why Choose Us data
const WHY_CHOOSE = [
  "Experienced & certified lash technicians",
  "Premium quality products & adhesives",
  "Long-lasting, lightweight & comfortable lashes",
  "Personalised style consultations",
  "Relaxing, clean & welcoming studio",
  "Convenient Maylands location",
];

// Star Rating Component
const StarRating = ({ rating }) => {
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className="w-5 h-5 fill-current star-filled"
        />
      ))}
      <span className="ml-2 text-lg font-semibold text-foreground">{rating}</span>
    </div>
  );
};

// Header/Navigation Component
const Header = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-card/95 backdrop-blur-md shadow-card py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex flex-col">
            <span className="text-xl md:text-2xl font-serif font-medium text-foreground tracking-wide">
              {BUSINESS_INFO.name}
            </span>
            <span className="text-xs text-muted-foreground tracking-[0.2em] uppercase">
              {BUSINESS_INFO.tagline}
            </span>
          </a>

          {/* Navigation - Desktop */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Services
            </a>
            <a href="#reviews" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Reviews
            </a>
            <a href="#contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              Contact
            </a>
          </nav>

          {/* Phone CTA - Desktop */}
          <a
            href={BUSINESS_INFO.phoneLink}
            className="hidden md:flex items-center gap-2 cta-button text-sm px-6 py-3"
          >
            <Phone className="w-4 h-4" />
            <span>Call Now</span>
          </a>

          {/* Mobile Phone Icon */}
          <a
            href={BUSINESS_INFO.phoneLink}
            className="md:hidden flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-button"
            aria-label="Call now"
          >
            <Phone className="w-5 h-5" />
          </a>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center hero-gradient overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-rose-gold/10 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left space-y-8 animate-fadeInUp">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-card shadow-soft">
              <StarRating rating={BUSINESS_INFO.rating} />
              <span className="text-sm text-muted-foreground">
                ({BUSINESS_INFO.reviewCount} reviews)
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-foreground leading-tight">
              Wake Up to
              <span className="block text-primary">Beautiful Lashes</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0">
              Experience premium eyelash extensions crafted with precision, artistry, and care. 
              Feel confident, feel beautiful — every single day.
            </p>

            {/* Phone Display */}
            <div className="space-y-4">
              <a
                href={BUSINESS_INFO.phoneLink}
                className="phone-display block hover:text-primary transition-colors"
              >
                <Phone className="inline-block w-7 h-7 mr-3 text-primary" />
                {BUSINESS_INFO.phone}
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href={BUSINESS_INFO.phoneLink} className="cta-button">
                <Phone className="w-5 h-5" />
                <span>Call to Book</span>
              </a>
              <a href="#services" className="cta-button-secondary">
                <span>View Services</span>
                <ChevronRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative animate-fadeInUp delay-200">
            <div className="relative rounded-3xl overflow-hidden shadow-elevated">
              <img
                src={BUSINESS_INFO.heroImage}
                alt="Beautiful eyelash extensions by Hertime"
                className="w-full h-[400px] lg:h-[550px] object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 to-transparent" />
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-6 -left-6 bg-card p-5 rounded-2xl shadow-elevated">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Premium Quality</p>
                  <p className="text-xs text-muted-foreground">Long-lasting results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="section-padding bg-card">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase">
            Our Services
          </span>
          <h2 className="section-title mt-4">Lash Styles for Every Look</h2>
          <div className="decorative-line" />
          <p className="section-subtitle mt-4 max-w-2xl mx-auto">
            From subtle enhancement to glamorous drama, we create personalised lash 
            styles that complement your unique features.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {SERVICES.map((service, index) => (
            <div
              key={index}
              className="feature-card text-center group flex flex-col"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <service.icon className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-serif font-medium text-foreground mb-3">
                {service.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed flex-grow">
                {service.description}
              </p>
              <a
                href={BUSINESS_INFO.phoneLink}
                className="mt-6 inline-flex items-center justify-center gap-2 text-primary font-medium hover:gap-3 transition-all"
              >
                <span>Book by Phone</span>
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <a href={BUSINESS_INFO.phoneLink} className="cta-button">
            <Phone className="w-5 h-5" />
            <span>Call to Discuss Your Style</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  return (
    <section className="section-padding soft-bg">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-elevated">
              <img
                src={BUSINESS_INFO.heroImage}
                alt="Hertime Eyelash Extensions studio"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 -bottom-8 -right-8 w-full h-full bg-primary/10 rounded-3xl" />
          </div>

          {/* Content */}
          <div className="space-y-8">
            <div>
              <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase">
                Why Choose Us
              </span>
              <h2 className="section-title mt-4">Experience the Hertime Difference</h2>
              <div className="decorative-line ml-0" />
            </div>

            <p className="section-subtitle">
              At Hertime Eyelash Extensions, we&apos;re dedicated to enhancing your natural 
              beauty with premium lash services that prioritise your comfort and satisfaction.
            </p>

            {/* Benefits List */}
            <ul className="space-y-4">
              {WHY_CHOOSE.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <a href={BUSINESS_INFO.phoneLink} className="cta-button inline-flex">
              <Phone className="w-5 h-5" />
              <span>Speak With Us</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  return (
    <section id="reviews" className="section-padding bg-card">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase">
            Client Love
          </span>
          <h2 className="section-title mt-4">What Our Clients Say</h2>
          <div className="decorative-line" />
          
          {/* Rating Summary */}
          <div className="mt-8 inline-flex items-center gap-4 px-6 py-3 rounded-full bg-secondary">
            <StarRating rating={BUSINESS_INFO.rating} />
            <span className="text-muted-foreground">Based on {BUSINESS_INFO.reviewCount} Google Reviews</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {REVIEWS.map((review, index) => (
            <div key={index} className="review-card flex flex-col">
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current star-filled" />
                ))}
              </div>
              
              {/* Review Text */}
              <p className="text-muted-foreground leading-relaxed flex-grow">
                &ldquo;{review.text}&rdquo;
              </p>
              
              {/* Reviewer */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="font-medium text-foreground">{review.name}</p>
                <p className="text-sm text-muted-foreground">{review.date}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-6">
            Ready to experience the difference? Call us today to book your appointment.
          </p>
          <a href={BUSINESS_INFO.phoneLink} className="cta-button">
            <Phone className="w-5 h-5" />
            <span>Call Now</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  return (
    <section id="contact" className="section-padding soft-bg">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase">
            Visit Us
          </span>
          <h2 className="section-title mt-4">Location & Hours</h2>
          <div className="decorative-line" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <div className="map-container h-[400px] bg-muted">
            <iframe
              src={BUSINESS_INFO.googleMapsUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Hertime Eyelash Extensions Location"
            />
          </div>

          {/* Info */}
          <div className="space-y-8">
            {/* Address Card */}
            <div className="feature-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-foreground mb-2">Our Location</h3>
                  <p className="text-muted-foreground">{BUSINESS_INFO.address}</p>
                </div>
              </div>
            </div>

            {/* Phone Card */}
            <div className="feature-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-foreground mb-2">Call Us</h3>
                  <a
                    href={BUSINESS_INFO.phoneLink}
                    className="text-xl font-semibold text-primary hover:text-primary-dark transition-colors"
                  >
                    {BUSINESS_INFO.phone}
                  </a>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="feature-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-grow">
                  <h3 className="font-serif text-lg font-medium text-foreground mb-4">Opening Hours</h3>
                  <div className="space-y-2">
                    {BUSINESS_INFO.hours.map((schedule, index) => (
                      <div
                        key={index}
                        className={`flex justify-between text-sm ${
                          schedule.time === "Closed"
                            ? "text-muted-foreground"
                            : "text-foreground"
                        }`}
                      >
                        <span>{schedule.day}</span>
                        <span className={schedule.time === "Closed" ? "" : "font-medium"}>
                          {schedule.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background with gradient overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BUSINESS_INFO.heroImage})` }}
      />
      <div className="absolute inset-0 bg-charcoal/80" />
      
      {/* Content */}
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-primary-foreground mb-6">
          Ready for Beautiful Lashes?
        </h2>
        <p className="text-lg text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
          Book your appointment today and discover why our clients love their lashes. 
          Call us now — we can&apos;t wait to meet you!
        </p>
        
        {/* Phone Display */}
        <a
          href={BUSINESS_INFO.phoneLink}
          className="inline-block text-3xl md:text-4xl font-semibold text-primary-foreground mb-8 hover:text-primary transition-colors"
        >
          <Phone className="inline-block w-8 h-8 mr-4" />
          {BUSINESS_INFO.phone}
        </a>
        
        {/* CTA Button */}
        <div>
          <a href={BUSINESS_INFO.phoneLink} className="cta-button text-lg px-10 py-5">
            <Phone className="w-6 h-6" />
            <span>Call to Book Now</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-charcoal text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-serif font-medium mb-4">
              {BUSINESS_INFO.name}
            </h3>
            <p className="text-primary-foreground/70 mb-6">
              Premium eyelash extensions in Maylands, Perth. 
              Enhancing your natural beauty, one lash at a time.
            </p>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-current star-filled" />
              ))}
              <span className="ml-2 text-primary-foreground/70">{BUSINESS_INFO.rating} ({BUSINESS_INFO.reviewCount} reviews)</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Contact</h4>
            <div className="space-y-4">
              <a
                href={BUSINESS_INFO.phoneLink}
                className="flex items-center gap-3 text-primary-foreground/70 hover:text-primary transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{BUSINESS_INFO.phone}</span>
              </a>
              <div className="flex items-start gap-3 text-primary-foreground/70">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{BUSINESS_INFO.address}</span>
              </div>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-semibold mb-4 text-lg">Hours</h4>
            <div className="space-y-2">
              {BUSINESS_INFO.hours.map((schedule, index) => (
                <div
                  key={index}
                  className="flex justify-between text-sm text-primary-foreground/70"
                >
                  <span>{schedule.day}</span>
                  <span>{schedule.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10 text-center">
          <p className="text-primary-foreground/50 text-sm">
            © {new Date().getFullYear()} {BUSINESS_INFO.name} {BUSINESS_INFO.tagline}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Sticky Mobile CTA
const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="sticky-cta">
      <a href={BUSINESS_INFO.phoneLink} className="sticky-cta-button">
        <Phone className="w-6 h-6" />
        <span>Call Now — {BUSINESS_INFO.phone}</span>
      </a>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <WhyChooseSection />
        <ReviewsSection />
        <LocationSection />
        <FinalCTASection />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}

export default App;
